import React from 'react';
import useAuthAndContent from './useAuthAndContent';
import useHandleApi from './useHandleApi';
import useTransition from './useTransition';
import useCart from './useCart';
import usePayment from './usePayment';
import useTransitCard from './useTransitCard';
import useAccount from './useAccount';
import useDeepLink from './useDeepLink';
import {locales, scope, processor} from '../settings';
import {initiateInactivityTimer} from './contextHelper';
import {fetchSingleOrderDetails as fetchSingleOrderDetailsAPI} from '../Api';

const Context = React.createContext('');
const EMPTY_CART_OBJ = {passes: [], storedValue: 0};
const INACTIVITY_SIGNOUT_TIME = 1000 * 60 * 15;
let inactivityData = {eventListenerCallback: null, timeouts: []};

function ContextProvider(props) {
  const [loading, setLoading] = React.useState(false);

  const authAndContent = useAuthAndContent({setLoading});

  const account = useAccount({loading, setLoading, authAndContent});

  const cart = useCart({
    EMPTY_CART_OBJ,
    authAndContent,
    account
  });

  const [handleApiCallWithToken, handleApiRes] = useHandleApi({
    authAndContent,
    setLoading,
    account
  });

  const fetchSingleOrderDetails = (...args) =>
    handleApiCallWithToken(fetchSingleOrderDetailsAPI, ...args);

  React.useEffect(() => {
    inactivityData = initiateInactivityTimer({
      token: authAndContent.token,
      signOut: authAndContent.signOut,
      timeouts: inactivityData.timeouts,
      eventListenerCallback: inactivityData.eventListenerCallback,
      INACTIVITY_SIGNOUT_TIME
    });
  }, [authAndContent.token]); // eslint-disable-line

  const payment = usePayment({
    account,
    handleApiCallWithToken,
    handleApiRes,
    authAndContent,
    setLoading,
    processor
  });

  const transitCard = useTransitCard(
    handleApiCallWithToken,
    handleApiRes,
    authAndContent
  );

  const transition = useTransition(handleApiCallWithToken);

  const deepLink = useDeepLink(account, authAndContent);

  return (
    <Context.Provider
      value={{
        locales,
        scope,
        processor,
        loading,
        setLoading,
        fetchSingleOrderDetails,

        getTicketName: authAndContent.getTicketName,
        locale: authAndContent.locale,
        setLocale: authAndContent.setLocale,
        localeText: authAndContent.localeText,
        content: authAndContent.content,
        showError: authAndContent.showError,
        snackBarMessage: authAndContent.snackBarMessage,
        setSnackBarMessage: authAndContent.setSnackBarMessage,

        loginUser: authAndContent.loginUser,
        token: authAndContent.token,
        getValidToken: authAndContent.getValidToken,
        registerAccount: authAndContent.registerAccount,
        forgotPassword: authAndContent.forgotPassword,
        resetPassword: authAndContent.resetPassword,
        verifyEmail: authAndContent.verifyEmail,
        resendVerificationEmail: authAndContent.resendVerificationEmail,
        signOut: authAndContent.signOut,
        saveToken: authAndContent.saveToken,

        fetchAccount: account.fetchAccount,
        account: account.account,
        updateAccount: account.updateAccount,
        updateAddress: account.updateAddress,

        selectedCardID: payment.selectedCardID,
        setSelectedCardID: payment.setSelectedCardID,
        paymentProcessing: payment.paymentProcessing,
        setPaymentProcessing: payment.setPaymentProcessing,
        oneTimePaymentToken: payment.oneTimePaymentToken,
        setOneTimePaymentToken: payment.setOneTimePaymentToken,
        oneTimePaymentTimeLeft: payment.oneTimePaymentTimeLeft,
        addOneTimePaymentMethod: payment.addOneTimePaymentMethod,
        removeOneTimePaymentMethod: payment.removeOneTimePaymentMethod,
        addPaymentMethod: payment.addPaymentMethod,
        removePaymentMethod: payment.removePaymentMethod,
        fetchPaymentToken: payment.fetchPaymentToken,
        existingPaymentMethodPurchase: payment.existingPaymentMethodPurchase,
        existingPaymentMethodAddBalance:
          payment.existingPaymentMethodAddBalance,
        oneTimePaymentPurchase: payment.oneTimePaymentPurchase,
        oneTimePaymentAddBalance: payment.oneTimePaymentAddBalance,
        createAutoLoadProfile: payment.createAutoLoadProfile,
        disableAutoLoadTransitAccount: payment.disableAutoLoadTransitAccount,
        createAutoRenewProfile: payment.createAutoRenewProfile,
        disableAutoRenewProfile: payment.disableAutoRenewProfile,

        cart: cart.cart,
        showCartIsEmpty: cart.showCartIsEmpty,
        addToCartPasses: cart.addToCartPasses,
        cartPassesSetQuantity: cart.cartPassesSetQuantity,
        clearCartPasses: cart.clearCartPasses,
        checkout: cart.checkout,
        setCheckout: cart.setCheckout,
        purchase: cart.purchase,
        setPurchase: cart.setPurchase,
        EMPTY_CART_OBJ,

        addTransitCard: transitCard.addTransitCard,
        updateTransitCard: transitCard.updateTransitCard,
        transferPass: transitCard.transferPass,

        transitionAccountLookup: transition.transitionAccountLookup,
        transitionGetSecurityQuestion: transition.transitionGetSecurityQuestion,
        transitionAnswerSecurityQuestion:
          transition.transitionAnswerSecurityQuestion,
        transitionGetProducts: transition.transitionGetProducts,
        transitionTransferProducts: transition.transitionTransferProducts,
        transitionConfirm: transition.transitionConfirm,

        mobileFilter: deepLink.mobileFilter
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

export {Context, ContextProvider};
